import AsyncStorage from '@react-native-async-storage/async-storage';
import type { UserProfile } from '@/state/userStore';
import { hasPremiumAccess } from '@/services/entitlements';

const KEY_PREFIX = '@tripflow_last_entitlement_ui_v1:';

function keyForUid(uid: string): string {
  return `${KEY_PREFIX}${uid}`;
}

export type LastKnownEntitlementUiPayload = {
  savedAt: number;
  isPremium?: boolean;
  planTier?: UserProfile['planTier'];
  entitlement?: UserProfile['entitlement'];
};

/**
 * Persist last-known premium fields for Profile display when offline.
 * No-ops if uid is missing or profile slice does not qualify as premium.
 */
export async function saveLastKnownEntitlementUi(
  uid: string | null | undefined,
  fields: Pick<UserProfile, 'isPremium' | 'planTier' | 'entitlement'>,
): Promise<void> {
  if (!uid || !hasPremiumAccess(fields)) {
    return;
  }
  try {
    const payload: LastKnownEntitlementUiPayload = {
      savedAt: Date.now(),
      isPremium: fields.isPremium,
      planTier: fields.planTier,
      entitlement: fields.entitlement,
    };
    await AsyncStorage.setItem(keyForUid(uid), JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

export async function loadLastKnownEntitlementUi(
  uid: string | null | undefined,
): Promise<LastKnownEntitlementUiPayload | null> {
  if (!uid) return null;
  try {
    const raw = await AsyncStorage.getItem(keyForUid(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastKnownEntitlementUiPayload;
    if (!parsed || typeof parsed.savedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Remove all per-user last-known entitlement blobs (e.g. on sign-out). */
export async function clearAllLastKnownEntitlementUiKeys(): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const toRemove = keys.filter((k) => k.startsWith(KEY_PREFIX));
    if (toRemove.length > 0) {
      await AsyncStorage.multiRemove(toRemove);
    }
  } catch {
    /* best-effort */
  }
}
